import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { ThemeProvider, useColorScheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Typography } from "@mui/material";
import { createBrandTheme, Logo, colors } from "brand";
import {
  AccountMenu,
  AppBar,
  AppFooter,
  AppLayout,
  CognitoAuthProvider,
  ProtectedRoute,
  CallbackPage,
  LogoutPage,
  LoggedOutPage,
  ActiveOrgProvider,
  ActiveShowProvider,
  NavRail,
  OrgSwitcher,
  ShowSwitcher,
  useActiveOrg,
  UserDisplayName,
  BuildBadge,
  UsaBadge,
} from "common-fe";
import { OrganizationApi, Configuration } from "account-common";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HistoryIcon from "@mui/icons-material/History";
import { useScoreApi, unwrap } from "./api/scoreApi";
import { AnnouncerProvider, useAnnouncerContext } from "./context/AnnouncerContext";
import { RingDataProvider, useRingData } from "./context/RingDataContext";
import AnnouncerRoleGate from "./components/AnnouncerRoleGate";
import AnnouncerFrame from "./components/AnnouncerFrame";
import DaySpine from "./components/DaySpine";
import LiveDesk from "./components/LiveDesk";
import LookupColumn from "./components/LookupColumn";
import StatusBar from "./components/StatusBar";
import WholeShowList from "./components/WholeShowList";
// #2664 -- URGENT fallback: OrderOfGo.jsx was deleted in 255f3240 when the
// four-band shell cut over, and its Cypress coverage went with it in
// ae15400e. Both are restored verbatim (not rewritten against the new
// shell) and mounted at /legacy so there is a known-working screen to fall
// back to if the four-band frame misbehaves at a live show.
import OrderOfGo from "./components/OrderOfGo";
import { handleLogout } from "./utils/logout";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Inject the org-list fetcher (built from the generated account-common client)
// into the shared ActiveOrgProvider so announcer-fe sends the active-org
// X-Org-Id header on every score-api call — same pattern as scribe-fe /
// scoreadmin-fe. Without this the API falls back to the token's first org and
// an announcer with several org memberships would see the wrong show list.
const AnnouncerActiveOrgProvider = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const listOrgs = useCallback(
    async (isSuper) => {
      if (!accessToken) return [];
      const api = new OrganizationApi(
        new Configuration({ basePath: ACCOUNT_API_BASE_URL, accessToken })
      );
      const res = isSuper
        ? await api.listAllOrganizations(true) // enabledOnly (#2074): switcher shows only superadminEnabled orgs
        : await api.listOrganizations();
      return res.data?.status === "success" && Array.isArray(res.data.data)
        ? res.data.data
        : [];
    },
    [accessToken]
  );
  return <ActiveOrgProvider listOrgs={listOrgs}>{children}</ActiveOrgProvider>;
};

// #2112 — shared active-show, org-scoped, the same pattern scoreadmin-fe's
// ScoreadminActiveShowProvider uses. listShows is the generated score client
// (already X-Org-Id scoped); reloads when the active org changes. Replaces
// the old kiosk InitialSetup screen: the show is now a free switch from the
// app bar's ShowSwitcher, not a one-time, PIN-gated pick.
//
// #2190 — no local caching: a listShows() failure surfaces to
// ActiveShowProvider's own catch, same as any other consumer of that shared
// provider. NOTE this fetch is NOT one-shot-per-org-change in practice:
// ActiveShowProvider's effect deps are [listShows, orgId], and this
// provider's listShows memoises down to [accessToken, activeOrgId], so it
// re-runs on every access-token renew too, not only an org switch. A
// renew-time failure used to blank shows/activeShow mid-session with no
// self-heal until the next renew (#2215); ActiveShowProvider now keeps the
// last-good `shows` for the same org across a failed reload instead of
// resetting to [], so a transient renew-time failure leaves the running
// order on screen (stale, not blank) until the next successful reload.
const AnnouncerActiveShowProvider = ({ children }) => {
  const scoreApi = useScoreApi();
  const { activeOrgId } = useActiveOrg();
  const listShows = useCallback(async () => {
    return unwrap(await scoreApi.shows.listShows());
  }, [scoreApi]);
  return (
    // #2222 — announcer opts into the date-window filter (AC4): live shows,
    // not every show the org has ever ingested.
    <ActiveShowProvider listShows={listShows} orgId={activeOrgId} filterActive>
      {children}
    </ActiveShowProvider>
  );
};

function BrandLogoMark() {
  const { t } = useTranslation("announcer");
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Logo
        style={{
          paddingLeft: 8,
          height: 40,
          width: "auto",
          marginRight: 16,
          flexShrink: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.title")}
      </Typography>
    </Box>
  );
}

// #2139 (docs/score/announcer-ui-redesign.md §4.1) — the shared NavRail is
// the ring switcher: one item per ring on the current show, icon-only at
// 72px, expanding to 200px on its own toggle (both widths and the expanded
// localStorage persistence come from common-fe's NavRailContext/NavRail —
// this app doesn't own any of that, see NavRail.jsx / NavRailContext.jsx).
// Ring is read/written straight off AnnouncerContext + RingDataContext, the
// SAME ungated switchRing the deleted arena Tabs row used to call (#926) —
// switching ring has never needed the PIN, and #2112 already removed the PIN
// entirely. This replaces the #1646 arena-tabs row that used to live inside
// OrderOfGo's sticky header; ring is navigation now, not part of one band's
// own header.
export const narrowSwitcher = (maxWidths) => (theme) => ({
  minWidth: 0,
  [theme.breakpoints.down("lg")]: {
    "& .MuiInputBase-root": { maxWidth: maxWidths },
  },
});

export function AnnouncerShell({ children }) {
  const { t } = useTranslation("announcer");
  const navigate = useNavigate();
  const { mode, setMode } = useColorScheme();
  const { switchRing, ringResolved } = useAnnouncerContext();
  const { rings, selectedRing } = useRingData();

  const handleThemeToggle = () => {
    setMode(mode === "dark" ? "light" : "dark");
  };

  // One nav item per ring; icon is the ring NUMBER (spec: "the icon is the
  // ring number"), rendered as a small numbered badge rather than a stock
  // MUI icon since there is no icon that means "ring 3". The active ring
  // gets the same foxOrange + black-text treatment the deleted arena Tabs
  // used (legible in both themes, avoids brand primary.main's near-black
  // grey on dark, #862); `text` is the ring name shown when the rail is
  // expanded, same "Ring N" label the old tabs used.
  // #862 gotcha: no badgeContent is used here (ring numbers are never zero
  // in practice), so there is nothing to guard with `|| undefined` — noted
  // per the card's explicit gotcha callout, not because a badge exists here.
  // #2581 (TRI-232) / #2665 — `ring` is FVD free text, not guaranteed to be
  // a bare digit ("Indoor 4 Bell", "Indoor 1 - Ring 1" are real values), so
  // the circle used to render that whole string clamped to one line with
  // ellipsis. That still overflowed the 32px circle's own legibility (two
  // different rings both rendered as "In..."), so #2665 stops rendering the
  // ring NAME in the circle at all. Instead the circle shows the ring's
  // 1-based position in `rings` (already sorted into a deterministic,
  // natural-numeric order by ringsFor, so the same ring keeps the same
  // number for the whole session — do not invent a second ordering). The
  // full ring name stays available via NavRail's collapsed-state tooltip
  // and via `text` in the expanded row label below.
  const navItems = rings.map((ring, ringIndex) => {
    const active = ring === selectedRing;
    const ringNumber = ringIndex + 1;
    return {
      icon: (
        <Box
          data-testid={`ring-tab-${ring}`}
          data-active={active}
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: "0.95rem",
            border: 1,
            borderColor: active ? colors.foxOrange : "divider",
            bgcolor: active ? colors.foxOrange : "transparent",
            color: active ? "#000" : "inherit",
          }}
        >
          {ringNumber}
        </Box>
      ),
      text: `${t("setup.ringLabel")} ${ring}`,
      // #926 — ungated: switching a ring has never needed the PIN, and
      // #2112 removed the PIN entirely, so there's nothing left to gate.
      onClick: () => {
        if (ring !== selectedRing) switchRing(ring);
      },
    };
  });

  // #2141 (docs/score/announcer-ui-redesign.md §5) — the whole-show list's
  // own NavRail item, below the per-ring items (pushed last so NavRail's
  // in-order render puts it after them).
  navItems.push({
    icon: (
      <Box data-testid="nav-whole-show-list" sx={{ display: "flex" }}>
        <ListAltIcon />
      </Box>
    ),
    text: t("wholeShowList.navLabel"),
    onClick: () => navigate("/list"),
  });

  // #2664 -- URGENT: announcer-fe is a kiosk, an announcer at a live show
  // cannot be expected to type a URL, so the /legacy fallback needs a real
  // affordance rather than just existing as a route. A NavRail item next to
  // the existing /list entry is the same pattern #2141 used for
  // WholeShowList, pushed last so it sits below the ring tabs and the whole
  // show list item.
  navItems.push({
    icon: (
      <Box data-testid="nav-legacy-oog" sx={{ display: "flex" }}>
        <HistoryIcon />
      </Box>
    ),
    text: t("legacy.navLabel"),
    onClick: () => navigate("/legacy"),
  });

  const accountMenuItems = [
    {
      icon: <SettingsIcon />,
      text: t("accountMenu.accountSettings"),
      onClick: () => {
        window.location.href = import.meta.env.VITE_ACCOUNT_FE_BASE_URL + "/settings";
      },
    },
    {
      icon: mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />,
      text: mode === "dark" ? t("accountMenu.lightMode") : t("accountMenu.darkMode"),
      onClick: handleThemeToggle,
    },
    {
      icon: <LogoutIcon />,
      text: t("accountMenu.signOut"),
      onClick: handleLogout,
    },
  ];

  // #3064 -- below lg (1200px) the bar cannot hold the org switcher, the show
  // switcher, the email and the account menu at their natural widths: at 768px
  // it needed ~1026px, pushing the account menu off-screen (and it overflowed
  // at 1024px with a long email too). So below lg: the email label is hidden
  // (the account menu stays), and the two switchers get a smaller maxWidth
  // (their inner text already ellipsizes, and the full name is on `title`).
  // Every rule is scoped to `down("lg")`, so >= 1200px keeps the shared
  // components' own widths (org 220, show 420) untouched. The wrappers are
  // announcer-only on purpose: OrgSwitcher/ShowSwitcher are shared by every FE.
  const appBarRightContent = (
    <Box
      data-testid="announcer-appbar-right"
      sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
    >
      <Box sx={narrowSwitcher({ xs: 110, md: 150 })}>
        <OrgSwitcher />
      </Box>
      {/* #2582 (TRI-233) -- announcer-fe only: move the "Show all past
          shows" toggle into the show dropdown instead of the navbar. */}
      <Box sx={narrowSwitcher({ xs: 170, md: 200 })}>
        <ShowSwitcher toggleInDropdown />
      </Box>
      <Box
        data-testid="announcer-appbar-user"
        sx={{ display: { xs: "none", lg: "block" }, minWidth: 0 }}
      >
        <UserDisplayName />
      </Box>
      <AccountMenu menuItems={accountMenuItems} />
    </Box>
  );

  return (
    <AppLayout
      appBar={
        <AppBar
          sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}
          rightContent={appBarRightContent}
          brandLogo={<BrandLogoMark />}
        />
      }
      navRail={
        <Box data-testid="oog-ring" data-ring-resolved={ringResolved} sx={{ height: "100%" }}>
          <NavRail navItems={navItems} backgroundColor={colors.darkGrey} color="#fff" />
        </Box>
      }
      appFooterEnabled={true}
      appFooter={
        <AppFooter sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} Fox Village.
          </Typography>
        </AppFooter>
      }
    >
      {children}
    </AppLayout>
  );
}

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  useColorScheme();

  return (
    <CognitoAuthProvider cognitoAuthConfig={cognitoAuthConfig} location={location}>
      <AnnouncerActiveOrgProvider>
        <Routes>
          <Route
            path="/callback"
            element={<CallbackPage navigateFunction={navigate} location={location} />}
          />
          <Route path="/logout" element={<LogoutPage navigateFunction={navigate} />} />
          <Route path="/logged-out" element={<LoggedOutPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AnnouncerRoleGate>
                  <AnnouncerActiveShowProvider>
                    <AnnouncerProvider>
                      {/* #2137 (docs/score/announcer-ui-redesign.md §11) — ONE
                          ring poll set for every band under this shell. Sits
                          inside AnnouncerProvider (it reads selectedShow/
                          selectedRing from there) and outside the Routes so a
                          later multi-band shell shares this same provider
                          instance rather than each band mounting its own. */}
                      <RingDataProvider>
                        <AnnouncerShell>
                          <Routes>
                            <Route path="/" element={<Navigate to="/oog" replace />} />
                            {/* #2145 (docs/score/announcer-ui-redesign.md §3) — the
                                four-band frame is now the main route. OrderOfGo.jsx
                                is deleted in this same pass, and so is the
                                provisional /frame route this used to live at
                                (#2138) — every band already has real content
                                (#2140/#2149/#2142), so there is no reason left for
                                the two routes to be separate. StatusBar (#2144)
                                re-homes here too, passed as the frame's own
                                statusBar slot (see AnnouncerFrame.jsx's own
                                comment for why it needed a new row rather than an
                                existing band). */}
                            <Route
                              path="/oog"
                              element={
                                <AnnouncerFrame
                                  statusBar={<StatusBar />}
                                  dayBand={<DaySpine />}
                                  liveDeskBand={<LiveDesk />}
                                  lookUpBand={<LookupColumn />}
                                />
                              }
                            />
                            {/* #2141 (docs/score/announcer-ui-redesign.md §5) —
                                the whole-show list: its own view, reached from
                                its own NavRail item, never mounted alongside
                                the four /oog bands (AC1 is true by
                                construction: this route and /oog are
                                mutually exclusive inside the same switch). */}
                            <Route path="/list" element={<WholeShowList />} />
                            {/* #2664 -- URGENT fallback. OrderOfGo.jsx is
                                restored verbatim from git show 255f3240^ (the
                                commit that deleted it to cut over to the
                                four-band frame above), not rewritten from the
                                four-band components -- the whole point of a
                                fallback is that it is the code known to work.
                                /oog stays the default route; this is a
                                fallback, not a revert. */}
                            <Route path="/legacy" element={<OrderOfGo />} />
                            <Route path="*" element={<Navigate to="/oog" replace />} />
                          </Routes>
                        </AnnouncerShell>
                      </RingDataProvider>
                    </AnnouncerProvider>
                  </AnnouncerActiveShowProvider>
                </AnnouncerRoleGate>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AnnouncerActiveOrgProvider>
    </CognitoAuthProvider>
  );
}

export default function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();
  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
      <UsaBadge />
      <BuildBadge />
    </ThemeProvider>
  );
}
